import { FieldProps, getIn } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { FieldContainer, FormikTextAreaField as Field, Label, Error } from '../../style'
import { messages } from './messages'

interface Props {
  id: string
  label?: string
  name: string
  disabled?: boolean
  required?: boolean
  placeholder?: string
  maxLength?: number
}

export const TextAreaField: React.FunctionComponent<Props & FieldProps> = ({
  id,
  field,
  form,
  label,
  name,
  placeholder,
  required,
  disabled = false,
  maxLength = 30000,
  ...rest
}) => {
  const { formatMessage } = useIntl()
  const [value, setValue] = useState<string>(field.value || '')
  const [truncated, setTruncated] = useState(false)
  const pasteExceededRef = useRef(false)

  useEffect(() => {
    setValue(field.value)
  }, [field.value])

  const handleChange = useCallback(
    (event: any) => {
      const targetValue = event.target.value

      setValue(targetValue)

      if (pasteExceededRef.current) {
        pasteExceededRef.current = false
        setTruncated(true)
      } else {
        setTruncated(false)
      }

      const trimmedValue = targetValue.trim()

      form.setFieldValue(field.name, trimmedValue)
      form.setFieldTouched(field.name, true)
    },
    [field, form]
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = event.clipboardData.getData('text/plain')
      const currentLength = event.currentTarget.value.length
      pasteExceededRef.current = currentLength + pastedText.length > maxLength
    },
    [maxLength]
  )

  const fieldError = getIn(form.errors, field.name)
  const fieldTouched = getIn(form.touched, field.name)
  const hasErrors = Boolean(fieldError) && Boolean(fieldTouched)
  const charCount = (value || '').length
  const showCounter = truncated || charCount > maxLength * 0.8

  return (
    <FieldContainer {...rest}>
      {label && <Label required={required}>{label}</Label>}

      <Field
        id={id}
        component="textarea"
        rows={4}
        name={name}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        onBlur={() => setTruncated(false)}
        onPaste={handlePaste}
        maxLength={maxLength}
      />
      {truncated && (
        <PasteTruncatedWarning>
          {formatMessage(messages.pasteTruncatedWarning, { maxLength })}
        </PasteTruncatedWarning>
      )}
      {!truncated && showCounter && <CharCount at_limit={charCount >= maxLength}>{charCount} / {maxLength}</CharCount>}
      {hasErrors && <Error className="error-tooltip">{fieldError}</Error>}
    </FieldContainer>
  )
}

const CharCount = styled.span<{ at_limit: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  font-size: 0.7rem;
  color: ${props => props.at_limit ? props.theme.colors.red100 : props.theme.colors.grey500};
  pointer-events: none;
`

const PasteTruncatedWarning = styled.span`
  position: absolute;
  bottom: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  background-color: ${props => props.theme.colors.yellow100};
  color: ${props => props.theme.colors.grey700};
  border-left: 3px solid ${props => props.theme.colors.yellow700};
  border-radius: ${props => props.theme.radius.medium};
  pointer-events: none;
`
